import type {
  BooleanFilterValue,
  DateFilterValue,
  DateRangeFilterValue,
  FilterValue,
  ListFilterValue,
  MonthYearFilterValue,
  NumericFilterValue,
  TextFilterValue,
} from './filterTypes';
import { TEXT_FILTER_OP } from './filterTypes';

/** Writes one filter value into `qs` under the given `param` key. */
type FilterSerializer<V extends FilterValue = FilterValue> = (
  qs: URLSearchParams,
  param: string,
  value: V,
) => void;
/** Erases the generic so the record can hold serializers for all kinds. */
type AnyFilterSerializer = FilterSerializer;

/**
 * Maps each filter kind to its URL-param serialization logic.
 *
 * text    → single param   ?lastName[like]=Dupont
 * date    → single param   ?entryDate[gte]=2024-01-01
 * numeric → single param   ?activityRate[lte]=100
 * boolean → single param   ?isActive[eq]=true
 * list    → single param   ?fieldCode[in]=CF_BAG1,CF_BAN1
 */
export const FILTER_SERIALIZERS: Record<FilterValue['kind'], AnyFilterSerializer> = {
  text: ((qs, param, v: TextFilterValue) =>
    qs.set(`${param}[${TEXT_FILTER_OP}]`, v.value)) as AnyFilterSerializer,
  date: ((qs, param, v: DateFilterValue) =>
    qs.set(`${param}[${v.op}]`, v.value)) as AnyFilterSerializer,
  // Date range → two bracket params (gte + lte); the backend ANDs them.
  // Empty bounds are skipped so a half-filled range still serializes cleanly.
  dateRange: ((qs, param, v: DateRangeFilterValue) => {
    if (v.from) qs.set(`${param}[gte]`, v.from);
    if (v.to) qs.set(`${param}[lte]`, v.to);
  }) as AnyFilterSerializer,
  numeric: ((qs, param, v: NumericFilterValue) =>
    qs.set(`${param}[${v.op}]`, String(v.value))) as AnyFilterSerializer,
  list: ((qs, param, v: ListFilterValue) =>
    qs.set(`${param}[${v.op}]`, v.values.join(','))) as AnyFilterSerializer,
  boolean: ((qs, param, v: BooleanFilterValue) =>
    qs.set(`${param}[eq]`, String(v.value))) as AnyFilterSerializer,
  // Exact month match on a YYYYMM column (LIKE with no wildcard = equality),
  // matching the backend's LIKE-only `mois` filter.
  monthYear: ((qs, param, v: MonthYearFilterValue) =>
    qs.set(`${param}[like]`, v.value)) as AnyFilterSerializer,
};
